import { useMemo } from 'react'

import { RADIUS_SLIDER_HALF_POINT_KMS } from '@/constants/location'
import { type SortByKeys, LOW_INVENTORY_LIMIT, SORT_BY_OPTIONS } from '@/constants/search'
import { filterExtendedSearchFromListings } from '@/domain/extendedSearch'
import { getLocationArea } from '@/domain/location/getLocationArea'
import { getExtendedRadius } from '@/domain/lowInventory'
import { getPageOffset } from '@/domain/srp/getPageOffset'
import { useGetOrganicListingsQuery } from '@/generated'
import { type GlobalLocation } from '@/lib/apollo/locationCache'
import { trackEvent } from '@/lib/ga'
import { getSearchPageType } from '@/lib/ga/events/search'
import { type Listing } from '@/types/search'
import sendToLogger from '@/utils/sendToLogger'

export type UseExtendedSearchProps = {
  categoryId: number
  keywords?: string
  limit: number
  location: GlobalLocation
  organicListings: Listing[]
  page: number
  sortKey: SortByKeys
  totalListingCount: number
}

export type UseExtendedSearchResponse = { extendedListings: Listing[] }

export const useExtendedSearch = ({
  categoryId,
  keywords,
  limit,
  location,
  organicListings,
  page,
  sortKey,
  totalListingCount,
}: UseExtendedSearchProps): UseExtendedSearchResponse => {
  /**
   * The extended search refetch should be done with the same parameters as the last search
   * Adding 50% increase in the radius parameter
   *  */
  const locationArea = useMemo(() => getLocationArea(location), [location])
  const extendedRadius = useMemo(
    () => getExtendedRadius(locationArea?.radius || RADIUS_SLIDER_HALF_POINT_KMS),
    [locationArea?.radius]
  )
  const sortOption = SORT_BY_OPTIONS[sortKey]

  const { data } = useGetOrganicListingsQuery({
    skip:
      !organicListings.length ||
      location.isRegion ||
      page > 1 ||
      totalListingCount > LOW_INVENTORY_LIMIT,
    variables: {
      input: {
        pagination: { limit, offset: getPageOffset(page, limit) },
        sorting: { by: sortOption.sort, direction: sortOption.order },
        searchQuery: {
          attributeFilters: [],
          categoryId,
          dateRangeFilters: [],
          keywords,
          location: {
            id: location.id,
            area: locationArea
              ? { ...locationArea, radius: locationArea.radius + extendedRadius }
              : undefined,
          },
          rangeFilters: [],
        },
      },
    },
    onCompleted: () =>
      trackEvent({
        action: getSearchPageType(keywords),
        label: `OutsideLocationAdsLoaded;distance_from_outside_location=${extendedRadius}`,
      }),
    onError: (error) => sendToLogger(error),
  })

  const listings = data?.searchResultsPageUpdate.results?.organic || []
  const extendedListings = filterExtendedSearchFromListings(organicListings, listings)

  return { extendedListings }
}
